import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { useFormContext, useWatch } from 'react-hook-form';
import { isValidName } from 'utils/validators';
import { passengerTypes, types } from 'types/passengers';
import PassengerType from 'components/ui/PassengerType';
import {
  FormContainer,
  HeaderContainer,
  BodyContainer,
  FieldContainer,
  StyledInput,
  StyledInputWrapper,
  StyledInputError,
  StyledLabel,
  CenteredFlexWrapper
} from './BookingPathPassengerDetails.styles';
import { InfantSelect } from './InfantDetailsForm.styles';

function InfantDetailsForm({ infantCount, eligiblePassengers, passengerCount, addedParentIds, intl }) {
  const {
    register,
    control,
    formState: { errors },
    getValues,
    setValue
  } = useFormContext();
  const watchedInfants = useWatch({ control, name: 'infants' });

  return (
    <>
      {Array.from({ length: infantCount }, (_, index) => {
        const parentId = watchedInfants?.[index]?.parentPassengerId;
        const isAdded = addedParentIds?.has(parentId);
        const firstNameError = errors?.infants?.[index]?.firstName;
        const lastNameError = errors?.infants?.[index]?.lastName;
        const parentError = errors?.infants?.[index]?.parentPassengerId;
        const { onChange: registerParentOnChange, ...parentRegister } = register(`infants.${index}.parentPassengerId`);

        if (isAdded) {
          return (
            <FormContainer key={index} isBackground={(passengerCount + index) % 2 === 0} data-cy={`infant-form-${index}`}>
              <HeaderContainer>
                <CenteredFlexWrapper>
                  <StyledLabel marginRight="8px">
                    {passengerCount + index + 1}. <PassengerType type={types.INFANT_IN_ARMS} />
                  </StyledLabel>
                </CenteredFlexWrapper>
              </HeaderContainer>
              <BodyContainer>
                <FieldContainer>
                  <StyledLabel>
                    {watchedInfants?.[index]?.firstName} {watchedInfants?.[index]?.lastName}
                  </StyledLabel>
                </FieldContainer>
              </BodyContainer>
            </FormContainer>
          );
        }

        return (
          <FormContainer key={index} isBackground={(passengerCount + index) % 2 === 0} data-cy={`infant-form-${index}`}>
            <HeaderContainer>
              <CenteredFlexWrapper>
                <StyledLabel marginRight="8px">
                  {passengerCount + index + 1}. <PassengerType type={types.INFANT_IN_ARMS} />
                </StyledLabel>
              </CenteredFlexWrapper>
            </HeaderContainer>
            <BodyContainer>
              <FieldContainer>
                <StyledLabel>
                  <FormattedMessage id="paxForm.firstName" />
                </StyledLabel>
                <StyledInputWrapper>
                  <StyledInput
                    data-lpignore="true"
                    type="text"
                    data-cy={`infant-${index}-firstName`}
                    isInvalid={!!firstNameError}
                    {...register(`infants.${index}.firstName`, {
                      required: 'paxForm.validator.invalidFirstName',
                      validate: (value) => isValidName(value) || 'paxForm.validator.invalidFirstName'
                    })}
                  />
                  {firstNameError && (
                    <StyledInputError data-cy={`error-infant-${index}-firstName`}>
                      <FormattedMessage id={firstNameError.message} />
                    </StyledInputError>
                  )}
                </StyledInputWrapper>
              </FieldContainer>
              <FieldContainer>
                <StyledLabel>
                  <FormattedMessage id="paxForm.lastName" />
                </StyledLabel>
                <StyledInputWrapper>
                  <StyledInput
                    data-lpignore="true"
                    type="text"
                    data-cy={`infant-${index}-lastName`}
                    isInvalid={!!lastNameError}
                    {...register(`infants.${index}.lastName`, {
                      required: 'paxForm.validator.invalidLastName',
                      validate: (value) => isValidName(value) || 'paxForm.validator.invalidLastName'
                    })}
                  />
                  {lastNameError && (
                    <StyledInputError data-cy={`error-infant-${index}-lastName`}>
                      <FormattedMessage id={lastNameError.message} />
                    </StyledInputError>
                  )}
                </StyledInputWrapper>
              </FieldContainer>
              <FieldContainer>
                <StyledLabel>
                  <FormattedMessage id="passengerDetails.infantGuardian" />
                </StyledLabel>
                <StyledInputWrapper>
                  <InfantSelect
                    data-cy={`infant-${index}-parentPax`}
                    {...parentRegister}
                    onChange={(e) => {
                      const newParentId = e.target.value;
                      const allInfants = getValues('infants') || [];
                      const conflictIndex = allInfants.findIndex((inf, i) => i !== index && inf.parentPassengerId === newParentId);
                      if (conflictIndex !== -1) {
                        const oldParentId = allInfants[index]?.parentPassengerId;
                        setValue(`infants.${conflictIndex}.parentPassengerId`, oldParentId, { shouldValidate: false });
                      }
                      registerParentOnChange(e);
                    }}
                  >
                    {eligiblePassengers.map((passenger, originalIndex) => {
                      const typeKey = passengerTypes[passenger.type];
                      const label = typeKey ? intl.formatMessage({ id: `passengerType.${typeKey}` }) : passenger.type;
                      return (
                        <option key={passenger.id} value={passenger.id}>
                          {`${originalIndex + 1}. ${label}`}
                        </option>
                      );
                    })}
                  </InfantSelect>
                  {parentError && (
                    <StyledInputError data-cy={`error-infant-${index}-parentPax`}>
                      <FormattedMessage id={parentError.message} />
                    </StyledInputError>
                  )}
                </StyledInputWrapper>
              </FieldContainer>
            </BodyContainer>
          </FormContainer>
        );
      })}
    </>
  );
}

InfantDetailsForm.propTypes = {
  infantCount: PropTypes.number.isRequired,
  eligiblePassengers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired
    })
  ).isRequired,
  passengerCount: PropTypes.number.isRequired,
  addedParentIds: PropTypes.instanceOf(Set),
  intl: intlShape.isRequired
};

export default injectIntl(InfantDetailsForm);
